export type HelpCategory = 'start' | 'demands' | 'projects' | 'payments' | 'resources' | 'creation';
export type HelpAction = { label: string; path: string; workspace?: boolean };
export type HelpQuestion = {
  id: string;
  category: HelpCategory;
  title: string;
  paragraphs: string[];
  keywords?: string;
  action?: HelpAction;
};

export const helpCategories: { id: HelpCategory; label: string; description: string }[] = [
  { id: 'start', label: '账号与入驻', description: '注册、身份、认证与账号安全' },
  { id: 'demands', label: '需求与联系', description: '发布需求、表达意向与合作沟通' },
  { id: 'projects', label: '项目与素材', description: '成员、任务、会议、文件版本与复用' },
  { id: 'payments', label: '平台服务', description: '合作边界、数字资源与会员服务' },
  { id: 'resources', label: '影视资源', description: '资源展示、发布审核与直接沟通' },
  { id: 'creation', label: 'AI 与推广', description: '创作版本、发布计划与使用范围' },
];

export const helpQuestions: HelpQuestion[] = [
  {
    id: 'digital-resources',
    category: 'resources',
    title: '如何查找数字素材并选择使用授权？',
    paragraphs: [
      '在“数字资源”按视频、图片、音频、模板或三维素材浏览，也可以搜索名称、标签、风格或制作软件。进入详情可播放预览、查看文件规格、软件依赖和权利声明。',
      '按实际用途选择个人、商业项目或扩展项目授权，再到素材工作台确认使用主体和项目。购买并确认付款后，在“我的购买”查看授权凭证和可下载文件；预览不等于获得源文件或商用许可。',
      '音乐、字体、肖像和第三方素材应结合许可范围逐项核对。项目“创作需求”可记录来源、范围和待补的授权，也能导出 Word 或 PDF 需求书。小程序提供数字素材浏览、搜索和预览。',
    ],
    keywords: '数字素材 视频 图片 音频 模板 授权 商用 购买 下载 版权 字体',
    action: { label: '浏览数字资源', path: '/digital-resources' },
  },
  {
    id: 'project-brief',
    category: 'projects',
    title: '如何整理创作需求并导出需求书？',
    paragraphs: [
      '创作需求用于对齐拍什么、给谁看、想表达什么，以及交付规格、时间与预算，业内也常称为 Brief。它为后续脚本、分镜和拍摄安排提供依据。',
      '在项目内打开“创作需求”，填写目标人群、营销目的、表达内容、成片时长、计划预算、交付规格与渠道。尚未确定的内容可以留空，并写入待确认事项。交付日期与项目基本资料同步。',
      '音乐、视频素材、字体、图片、肖像与商标等默认待核对。标记可商用时，补充权利来源与允许的使用范围；项目中的授权凭证可作为核对依据。',
      '保存后点击 Word 或 PDF，即可导出本次保存的创作需求书。小程序项目概览也有“创作需求”入口。AI 工作台中已保存的创作需求同样支持导出。',
    ],
    keywords: 'Brief 需求 目标人群 时长 预算 营销 知识产权 导出 docx pdf',
    action: { label: '打开我的项目', path: '/projects', workspace: true },
  },

  {
    id: 'choose-role',
    category: 'start',
    title: '注册时应该选择哪种身份？',
    paragraphs: [
      '如果你为企业或品牌寻找制作团队，选择采购方；如果你提供视频制作、拍摄或后期服务，选择服务商；如果你提供场地、演员、摄影师、配音、器材等，选择资源方。',
      '注册后会创建你的组织。一个账号可以加入多个组织；进入工作台时先确认当前组织，再切换该组织已开通的业务身份。组织身份和你在组织中的成员权限分别管理。',
    ],
    keywords: '客户 甲方 乙方 公司 团队 工作台 组织 身份切换',
    action: { label: '注册影爸爸账号', path: '/login?mode=register&role=client' },
  },
  {
    id: 'provider-onboarding',
    category: 'start',
    title: '服务商和资源方如何入驻？',
    paragraphs: [
      '服务商注册后，在工作台「作品与服务」完善团队介绍，提交服务商主页，并准备真实案例与服务产品。资料提交后进入审核，通过后才在公开市场展示。',
      '资源方在「我的影视资源」填写资源分类、城市、介绍和联系说明，提交审核。案例与资源展示请使用有权发布的图片、视频链接和说明。',
      '「主体认证」用于确认个人或企业主体，与主页、案例和资源的业务审核分开。认证状态以工作台实际展示为准，审核反馈可在相关记录和系统通知中查看。',
    ],
    keywords: '入驻 审核 服务产品 作品 案例 认证 公司资料',
    action: { label: '查看主体认证', path: '/verification', workspace: true },
  },
  {
    id: 'organization-lifecycle',
    category: 'start',
    title: '如何交接组织所有者、停用组织或注销账号？',
    paragraphs: [
      '组织所有者在工作台「团队与组织」选择有效现有成员发起交接，接任者需登录自己的账号、重新验证身份并明确接受；交接申请在 72 小时内有效。交接完成后原所有者变为管理员，接任者成为唯一所有者。小程序可在「我的 → 组织管理」操作。',
      '账号注销在「账号设置」发起。申请前须处理所属组织的所有权、未结项目和平台管理职责，页面会列出阻塞事项。默认有 7 天冷静期，期间可撤销；生效后账号不可登录。组织所有者可停用组织，停止新业务但保留记录，也可恢复；删除组织默认等待 30 天，页面显示当前配置和阻塞事项。',
      '删除会先处理公开资料，非交易私有资料按后台留存政策清理。未结资金、争议和资料保全会阻止处理，共享交易证据不会因一方删除组织而丢失。请先核对页面显示的期限，重要资料提前保存。',
    ],
    keywords: '注销账号 删除组织 停用组织 恢复组织 所有者 交接 数据留存 冷静期',
    action: { label: '管理账号与注销申请', path: '/account', workspace: true },
  },
  {
    id: 'email-verification',
    category: 'start',
    title: '为什么需要验证邮箱？邮箱填错了怎么办？',
    paragraphs: [
      '注册后会向登录邮箱发送验证邮件，链接在 24 小时内有效且只能使用一次。验证前可以浏览、准备资料和草稿、处理既有项目；发布需求、服务商资料、资源、案例和服务产品，公开表达合作意向前，需要先验证登录邮箱。',
      '在工作台「账号设置」或小程序「我的 → 登录邮箱」可以重发验证邮件。每 60 秒可发送一次，每 24 小时最多 10 次，请检查邮箱拼写、垃圾邮件和过滤规则，使用最近一封邮件中的链接。邮件发送失败不会撤销已创建的账号，请直接登录后重发，不必重复注册。',
      '邮箱填错或需要更换时，请验证当前密码；已开启双因素认证的账号还需验证码或恢复码。确认邮件会发送到新邮箱，打开链接并确认后才会切换登录地址。完成前原邮箱仍可登录；完成后全部旧会话和旧安全链接失效，请使用新邮箱重新登录。',
    ],
    keywords: '邮箱验证 收不到邮件 填错邮箱 修改邮箱 更换邮箱 注册 验证链接',
    action: { label: '管理登录邮箱', path: '/account', workspace: true },
  },
  {
    id: 'password-reset',
    category: 'start',
    title: '忘记密码或收不到重置邮件怎么办？',
    paragraphs: [
      '在登录页选择「忘记密码」，填写注册时使用的邮箱。请检查收件箱、垃圾邮件及邮件过滤规则，确认邮箱拼写正确；重置链接有效期为 30 分钟，成功使用后即失效。',
      '重复申请会使此前未使用的重置链接失效，请使用最近一封邮件中的链接。修改密码后，其他设备需要重新登录；已开启的双因素认证仍需完成验证。',
      '为了保护账号隐私，页面提示已提交不代表该邮箱一定注册过。若页面提示邮件发送失败或长期无法收到邮件，请通过本页联系平台，提供发生时间和问题截图，勿发送密码、验证码或重置链接。',
    ],
    keywords: '找回密码 忘记密码 邮箱 SMTP 邮件发送失败 登录 授权码',
    action: { label: '前往登录页找回密码', path: '/login?mode=forgot' },
  },
  {
    id: 'account-security',
    category: 'start',
    title: '如何管理双因素认证和登录设备？',
    paragraphs: [
      '在工作台「账号设置」中绑定认证器，设置和启用时都需要验证当前密码。启用后保存恢复码；每个恢复码只能使用一次，请与登录密码分开保管。',
      '你可以查看登录会话并撤销其他设备的访问。关闭双因素认证需要当前密码和认证器验证码或恢复码；若两者都无法提供，请联系平台核验账号归属。',
    ],
    keywords: 'MFA 二次验证 验证器 恢复码 会话 设备 安全 退出',
    action: { label: '打开账号设置', path: '/account', workspace: true },
  },
  {
    id: 'wechat-account',
    category: 'start',
    title: '微信登录和手机号绑定如何使用？',
    paragraphs: [
      '微信小程序可将微信身份绑定到已有影爸爸账号，或注册账号后绑定。请始终核对要关联的邮箱账号，避免将不同账号当作同一个账号使用。',
      '网站微信扫码登录以登录页实际开放的入口为准；如果没有该入口，请使用邮箱和密码。服务号扫码登录开放后，请使用微信扫码，按微信提示关注公众号，再打开收到的确认链接。核对电脑上的识别码后确认；如果不是本人操作，请选择拒绝。仅关注或扫码不会自动登录。',
      '在小程序“我的 → 账号手机号”中，可主动授权绑定微信验证的号码，用于账号身份验证及业务联系。每个号码只能绑定一个账号，可重新授权、更换或解绑，界面仅显示脱敏号码。请先同意手机号用途说明及微信隐私保护指引。短信验证码登录尚未开放，当前仍使用邮箱或微信登录。',
    ],
    keywords: '微信 扫码 手机号 绑定 小程序 手机 电话',
  },
  {
    id: 'publish-demand',
    category: 'demands',
    title: '发布拍片需求前要准备什么？',
    paragraphs: [
      '先整理品牌或项目背景、传播目标、受众、视频类型、预算范围、拍摄城市、交付日期和预期成片时长。参考作品、已有素材、必拍内容及版权要求也可以一并说明。',
      '需求提交后进入审核。审核通过后才在需求大厅公开，服务商可报名并与你沟通合作；请在「我的需求」跟进状态和审核反馈。',
      '从案例或服务产品进入发布页时，可沿用对应的参考信息；请根据自己的业务补充实际要求，不要直接把参考案例当作最终交付约定。',
    ],
    keywords: 'Brief 客户 发布 需求 审核 预算 时长 拍片',
    action: { label: '发布拍片需求', path: '/publish' },
  },
  {
    id: 'compare-proposals',
    category: 'demands',
    title: '如何与团队沟通并建立项目？',
    paragraphs: [
      '需求发布后，可在需求工作区查看报名团队，阅读经验说明并进入独立会话。客户和团队先自行确认合作意向，再创建协作项目。',
      '在“我的项目”创建项目，填写目标与计划日期。可搜索团队或资源方，也可以使用合作方分享的组织协作码发送邀请；对方管理员接受后即可开始协作。',
    ],
    keywords: '如何与团队沟通并建立项目？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'service-price',
    category: 'demands',
    title: '服务产品可以帮助确认哪些合作范围？',
    paragraphs: [
      '服务产品用于说明预计周期、团队设备、修改轮次、制作内容及版权范围。先了解这些信息，再与服务商沟通实际工作要求。',
      '沟通时建议确认具体工作内容、费用、修改轮次、时间安排及使用权，避免对服务范围产生不同理解。',
    ],
    keywords: '价格 总价 起价 套餐 服务产品 修改 次数 加价',
    action: { label: '寻找制作团队', path: '/providers' },
  },
  {
    id: 'project-navigation',
    category: 'projects',
    title: '项目工作区有哪些内容？',
    paragraphs: [
      '项目分为概览、协作和素材。概览管理目标、阶段与进展；协作集中处理团队、任务、会议与动态；素材保留文件及历史版本。',
      '在“我的项目”点击“创建项目”，填写名称、目标与计划日期，再邀请团队成员。项目结束后可以归档，需要继续时可重新打开。',
    ],
    keywords: '项目工作区有哪些内容？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'project-change',
    category: 'projects',
    title: '项目目标、成员或计划变化时怎么办？',
    paragraphs: [
      '项目发起组织的管理员可以修改名称、说明、计划日期和归档状态。用任务、里程碑与会议纪要记录具体安排。',
      '成员通过明确邀请加入。可邀请客户、创作团队或资源方；外部组织接受后才获得访问权，移除组织会撤销后续访问并清空未完成任务中的相关负责人。',
    ],
    keywords: '项目目标、成员或计划变化时怎么办？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'delivery-acceptance',
    category: 'projects',
    title: '文件如何上传新版本并在其他项目复用？',
    paragraphs: [
      '在项目素材中选择“上传新版本”，填写版本说明后上传。旧版本独立保留，可随时预览和下载，不覆盖历史文件。',
      '点击“从素材库引入”，选择本组织上传且你有权访问的文件，确认共享权限后即可添加，无需再次上传。源文件后续更新不会影响本项目中已选用的版本。',
    ],
    keywords: '文件如何上传新版本并在其他项目复用？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'meetings',
    category: 'projects',
    title: '能在平台开视频会议吗？',
    paragraphs: [
      '平台可在项目内记录腾讯会议、飞书会议等第三方会议链接、时间、纪要与行动项。会议参与方在对应会议应用中进入通话，讨论后回到平台整理确认事项和任务。',
      '保存纪要后可生成 AI 行动项草稿，人工核对任务标题、负责人和截止日期，再逐项确认转为项目任务。未确认的信息会标为待确认。纪要修改后须重新生成，重复导入同一行动项不会重复创建任务；真实模型调用可能产生费用。',
      '请核对会议链接与参会权限。平台内的讨论、任务和会议纪要便于持续协作；重要费用、交付或版权变更还应形成正式约定。',
    ],
    keywords: '视频会议 腾讯会议 飞书 通话 纪要 沟通',
  },
  {
    id: 'payment-stages',
    category: 'payments',
    title: '制作服务和非数字资源如何交易？',
    paragraphs: [
      '影视制作及场地、人员、器材等资源合作，由双方直接商定服务范围、时间、费用、合同与付款安排。建议保留书面约定及实际凭证。影爸爸提供信息展示、沟通与项目协作服务，不代收这些合作的款项。',
      '数字资源授权和付费会员是独立服务，其价格和开通条件以各自页面及专项规则为准。',
    ],
    keywords: '制作服务和非数字资源如何交易？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'payment-pending',
    category: 'payments',
    title: '项目结束后如何归档或重新打开？',
    paragraphs: [
      '由项目发起方的管理员进入“项目概览 → 编辑项目”，将状态改为“已归档”并保存，便于整理已结束的工作。',
      '归档后仍可查看已有文件和协作记录。需要继续工作时，把状态改回“进行中”即可恢复编辑。',
    ],
    keywords: '项目结束后如何归档或重新打开？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'commission',
    category: 'payments',
    title: '目前会对影视合作抽佣吗？',
    paragraphs: [
      '当前影视制作与非数字资源合作不通过平台形成收款和抽佣流程，交易安排由合作双方自主决定。',
      '数字资源销售的供稿协议、佣金与结算规则单独管理；付费会员也按会员页面的独立服务规则处理。',
    ],
    keywords: '目前会对影视合作抽佣吗？ 合作 项目 工具 版本 素材',
    action: {
      label: '阅读交易与费用约定',
      path: '/terms#legal-section-4',
    },
  },
  {
    id: 'cancel-refund',
    category: 'payments',
    title: '合作取消或发生争议如何处理？',
    paragraphs: [
      '制作服务和非数字资源的取消、费用、退款与争议由合作双方依照实际约定处理。影爸爸不替双方认定履约、验收、扣费或退款。',
      '建议保留双方约定、沟通内容和相关文件，先与合作方协商处理。账号、功能使用或内容问题可联系平台客服。',
    ],
    keywords: '合作取消或发生争议如何处理？ 合作 项目 工具 版本 素材',
    action: {
      label: '前往沟通中心',
      path: '/messages',
      workspace: true,
    },
  },
  {
    id: 'contracts-invoices',
    category: 'payments',
    title: '合同、付款和发票如何安排？',
    paragraphs: [
      '合作双方自行确认签约、付款及发票主体，以及服务范围、素材使用权、修改轮次和责任划分。平台项目工具不替代双方合同。',
      '可把双方有权共享的文件保存在项目素材中，使用版本说明和会议记录帮助团队查阅。',
    ],
    keywords: '合同、付款和发票如何安排？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'resource-inquiry',
    category: 'resources',
    title: '如何联系场地、演员、器材等资源方？',
    paragraphs: [
      '在影视资源中按分类、城市或关键词查找，进入详情后点击“联系资源方”。登录后会进入与该资源相关的独立会话。',
      '沟通时请说明使用时间、地点、工作内容和预算，并与资源方确认可用条件。',
    ],
    keywords: '如何联系场地、演员、器材等资源方？ 合作 项目 工具 版本 素材',
    action: {
      label: '寻找影视资源',
      path: '/resources',
      workspace: false,
    },
  },
  {
    id: 'quote-expiry',
    category: 'resources',
    title: '同一资源的沟通如何关联？',
    paragraphs: [
      '会话关联具体资源与发起人，优先联系资源发布人；未记录发布人或联系人失效时，使用该组织的有效管理员作为联系人。',
      '不同客户和不同资源的会话分开，不会自动共享其他客户的咨询历史。联系人变更也不默认向新联系人开放旧的私聊记录。',
    ],
    keywords: '同一资源的沟通如何关联？ 合作 项目 工具 版本 素材',
    action: {
      label: '寻找影视资源',
      path: '/resources',
      workspace: false,
    },
  },
  {
    id: 'resource-order',
    category: 'resources',
    title: '资源合作需要在平台下单吗？',
    paragraphs: [
      '不需要。非数字资源只提供介绍、审核展示与直接沟通入口，合作双方自主安排交易。',
      '有协作需要时，可在项目中邀请资源方组织。管理员接受邀请后，团队就能共同跟进任务、会议与素材。',
    ],
    keywords: '资源合作需要在平台下单吗？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'ai-capabilities',
    category: 'creation',
    title: 'AI 创作现在可以完成哪些工作？',
    paragraphs: [
      '当前提供创作需求、视频脚本、文字分镜和营销文案的整理与生成，支持编辑、保存版本及导出文本。生成结果需要你核对事实、品牌口径、素材权利和可执行性，再用于提案或生产。',
      '图片绘制、成片生成、视频剪辑与自动切片需要使用相应制作工具。影爸爸当前的 AI 工作台主要帮助你形成可讨论的文字方案；当生成服务不可用时，可先建立和编辑结构化草稿。',
    ],
    keywords: 'AI 人工智能 Brief 脚本 分镜 文案 图片 视频 切片 生成',
    action: { label: '了解 AI 创作', path: '/ai' },
  },
  {
    id: 'ai-retry',
    category: 'creation',
    title: 'AI 任务失败或内容不满意怎么办？',
    paragraphs: [
      '在创作工作台查看任务状态与失败提示。失败或停止的任务可点击“重新生成”；生成中可停止当前任务。已发生的模型调用可能仍产生费用。你也可以调整输入后创建一份新创作，原任务记录会保留。输入品牌背景、目标受众、用途、时长和必须保留的信息，通常比只输入一句主题更便于得到具体方案。',
      '对已完成内容可以手动修改并保存为新版本；保存版本后保留原稿，方便团队比较。若提示额度或能力暂不可用，请先编辑草稿，或联系平台确认当前可用服务。',
    ],
    keywords: 'AI 失败 重试 额度 配额 版本 编辑 导出',
    action: { label: '打开创作工作台', path: '/ai', workspace: true },
  },
  {
    id: 'promotion-publish',
    category: 'creation',
    title: '如何用项目素材准备视频推广？',
    paragraphs: [
      '创建推广草稿，选择目标平台与来源项目，选择要发布的图片、视频、音频或字幕，并确认发布授权。可以单独选择封面和自己维护的发布账号。',
      '在“素材与发布准备”中预览或下载文件、复制文案与发布清单，按渠道提示前往官方创作中心发布。发布后保存作品链接，记录渠道后台的播放、互动等数据。',
      '发布账号用于安排内容去向，不需要提供密码。在影爸爸完成准备后，前往对应平台发布，再回来保存链接和效果记录。',
    ],
    keywords: '如何用项目素材准备视频推广？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开推广工作台',
      path: '/promotion',
      workspace: true,
    },
  },
  {
    id: 'review-response',
    category: 'projects',
    title: '如何邀请客户或其他团队参与项目？',
    paragraphs: [
      '由项目发起方的管理员在“项目概览 → 协作伙伴”点击“邀请合作方”，搜索团队、资源方，或粘贴对方分享的组织协作码。协作码可在网页“组织与团队”或小程序“我的”中复制。',
      '对方管理员接受邀请后，可以查看项目并添加本组织成员。需要调整合作范围时，可移除合作方；对方也可主动退出。已保存的协作记录会保留。',
    ],
    keywords: '如何邀请客户或其他团队参与项目？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'repeat-project',
    category: 'projects',
    title: '下一次制作可以沿用已有素材吗？',
    paragraphs: [
      '创建新的协作项目后，打开项目素材，从组织素材库选择要使用的文件版本并确认共享权限。文件直接复用，不必重复上传。',
      '每个项目的文件版本独立管理。需要使用更新后的源文件时，重新选择明确的新版本，不会自动覆盖旧项目中的内容。',
    ],
    keywords: '下一次制作可以沿用已有素材吗？ 合作 项目 工具 版本 素材',
    action: {
      label: '打开我的项目',
      path: '/projects',
      workspace: true,
    },
  },
  {
    id: 'resource-calendar',
    category: 'resources',
    title: '如何与资源方确认使用时间？',
    paragraphs: [
      '在资源详情点击“联系资源方”，说明预计使用日期、时段、地点和数量，请对方确认是否可用。',
      '确认合作后，可在项目任务、会议或里程碑中记录安排，方便团队共同查阅；时间变化时请及时与资源方重新确认。',
    ],
    keywords: '如何与资源方确认使用时间？ 合作 项目 工具 版本 素材',
    action: {
      label: '寻找影视资源',
      path: '/resources',
      workspace: false,
    },
  },
  {
    id: 'resource-fees',
    category: 'resources',
    title: '押金、延期及损坏费用如何处理？',
    paragraphs: [
      '非数字资源的费用、押金、延期、超时和损坏责任由双方在实际合作中自行约定与处理，平台不代收、扣抵或认定这些费用。',
      '建议在使用前书面确认计费、延期及赔付规则，并把有权共享的约定存入项目，方便团队查阅。',
    ],
    keywords: '押金、延期及损坏费用如何处理？ 合作 项目 工具 版本 素材',
    action: {
      label: '寻找影视资源',
      path: '/resources',
      workspace: false,
    },
  },
];

export function searchHelp(category: string, query: string) {
  const words = query
    .normalize('NFKC')
    .trim()
    .toLocaleLowerCase('zh-CN')
    .split(/\s+/)
    .filter(Boolean);
  return helpQuestions.filter((question) => {
    if (category && category !== question.category) return false;
    const content = [question.title, ...question.paragraphs, question.keywords || '']
      .join(' ')
      .normalize('NFKC')
      .toLocaleLowerCase('zh-CN');
    return words.every((word) => content.includes(word));
  });
}
